// DiffTab — comparison results view with side-by-side document viewer
// (Phase 3G + docs-diff-viewer).
//
// Layout: header (original -> revision + prev/next nav) on top, then a
// three-column area: original text (left), revision text (center),
// differences list (right). Clicking a difference scrolls both document
// panes to the relevant page and flashes it.

import { useEffect, useRef, useState } from 'react';
import {
  Plus,
  Minus,
  Pencil,
  Loader2,
  ExternalLink,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Download,
  Clipboard,
} from 'lucide-react';
import { getCompareJob, exportCompare, type CompareDifference } from '../api/datawash';
import { getDocumentText, type DocumentText } from '../api/cases';
import { renderMarkdown } from '../utils/markdown';
import SideBySideViewer, {
  type SideBySideViewerHandle,
} from './SideBySideViewer';

interface DiffTabProps {
  caseId: string;
  compareJobId: string | null;
  refreshKey?: number;
  onSourceLink?: (link: string) => void;
  originalFilename?: string;
  revisionFilenames?: string[];
  /** Original document id (for fetching extracted text). */
  originalDocId?: string;
  /** Revision document id (for fetching extracted text). */
  revisionDocId?: string;
}

const TYPE_META: Record<
  CompareDifference['type'],
  { label: string; icon: typeof Plus; cls: string }
> = {
  added: { label: 'Added', icon: Plus, cls: 'bg-green-100 text-green-800' },
  removed: { label: 'Removed', icon: Minus, cls: 'bg-red-100 text-red-800' },
  changed: { label: 'Changed', icon: Pencil, cls: 'bg-amber-100 text-amber-800' },
};

export default function DiffTab({
  caseId: _caseId,
  compareJobId,
  refreshKey,
  originalFilename,
  revisionFilenames,
  originalDocId,
  revisionDocId,
}: DiffTabProps) {
  const [status, setStatus] = useState<string>('pending');
  const [differences, setDifferences] = useState<CompareDifference[]>([]);
  const [summary, setSummary] = useState<string>('');
  const [origName, setOrigName] = useState<string | undefined>(originalFilename);
  const [revNames, setRevNames] = useState<string[] | undefined>(revisionFilenames);
  // Track doc-id props directly (not frozen useState) so a comparison tab
  // that mounts before the job completes still picks up the ids once the
  // results call lands.
  const origDocId = originalDocId;
  const revDocId = revisionDocId;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeDiff, setActiveDiff] = useState(0);
  const [origText, setOrigText] = useState<DocumentText | null>(null);
  const [revText, setRevText] = useState<DocumentText | null>(null);
  const [exporting, setExporting] = useState(false);
  const viewerRef = useRef<SideBySideViewerHandle>(null);

  // Load comparison job + poll until it reaches a terminal state
  // (complete / failed / cancelled). A comparison tab mounts as soon as
  // the user hits Compare, while the job is still pending, so a one-shot
  // fetch would freeze on 'in progress' forever. Poll every 2s.
  useEffect(() => {
    if (!compareJobId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    let done = false;
    let firstPass = true;
    const poll = async () => {
      try {
        const job = await getCompareJob(compareJobId);
        if (cancelled) return;
        setStatus(job.status);
        setDifferences(job.results?.differences || []);
        setSummary(job.results?.summary || '');
        if (job.results?.original_filename) setOrigName(job.results.original_filename);
        if (job.results?.revision_filenames) setRevNames(job.results.revision_filenames);
        if (job.error) setError(job.error);
        if (job.status === 'complete' || job.status === 'error' || (job.status as string) === 'failed' || (job.status as string) === 'cancelled') {
          done = true;
        }
      } catch (e: any) {
        if (cancelled) return;
        if (e?.response?.status === 404) {
          setError('Feature pending: comparison results not available yet.');
        } else {
          setError(e?.response?.data?.detail || 'Failed to load comparison');
        }
        done = true;
      } finally {
        // Clear the initial loading spinner after the first fetch so the
        // 'in progress' state can render; keep polling until terminal.
        if (firstPass) { firstPass = false; setLoading(false); }
      }
    };
    setLoading(true);
    poll();
    const handle = setInterval(() => { if (!done && !cancelled) poll(); }, 2000);
    return () => { cancelled = true; clearInterval(handle); };
  }, [compareJobId, refreshKey]);

  // Load document texts once we have doc ids.
  useEffect(() => {
    if (!origDocId && !revDocId) return;
    if (origDocId) {
      getDocumentText(origDocId)
        .then(setOrigText)
        .catch(() => setOrigText(null));
    }
    if (revDocId) {
      getDocumentText(revDocId)
        .then(setRevText)
        .catch(() => setRevText(null));
    }
  }, [origDocId, revDocId, status]);

  // Scroll both document panes to the active difference's area when the
  // selected difference changes. We scroll to the Nth non-equal block in
  // the client-side line diff (the block ordinal approximates the backend
  // difference ordinal), which lands on the actual changed lines rather
  // than just the page marker.
  useEffect(() => {
    if (status !== 'complete' || differences.length === 0) return;
    viewerRef.current?.scrollToDifferenceBlock(activeDiff);
  }, [activeDiff, differences, status]);

  if (!compareJobId) {
    return (
      <div
        className="p-8 text-center text-slate-400 text-sm"
        data-testid="diff-tab-empty"
      >
        No comparison has been run yet. Upload an original and a revised
        version to compare.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center gap-2 text-slate-400" data-testid="diff-tab">
        <Loader2 size={16} className="animate-spin" /> Loading comparison...
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="p-4 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg m-4"
        data-testid="diff-tab-error"
      >
        {error}
      </div>
    );
  }

  if (status !== 'complete') {
    return (
      <div
        className="p-8 text-center text-slate-500 text-sm"
        data-testid="diff-tab-pending"
      >
        Comparison in progress: {status}...
      </div>
    );
  }

  const hasViewer = origText && revText;
  const revDisplayName =
    revNames && revNames.length > 0 ? revNames[0] : 'Revision';

  const goToDiff = (idx: number) => {
    const clamped = Math.max(0, Math.min(idx, differences.length - 1));
    setActiveDiff(clamped);
  };

  const exportDiff = () => {
    const lines = [
      `Comparison: ${origName || 'Original'} -> ${revDisplayName}`,
      summary || '',
      '',
      ...differences.map((d, i) => {
        const meta = TYPE_META[d.type] || TYPE_META.changed;
        return `${i + 1}. [${meta.label}] Page ${d.page}: ${d.description}`;
      }),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `diff_${origName || 'original'}_vs_${revDisplayName}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Backend-powered export (UC-2.6): PDF download + clipboard copy, with a
  // client-side text fallback when the export endpoint is unavailable.
  const exportPdf = async () => {
    if (!compareJobId) { exportDiff(); return; }
    setExporting(true);
    try {
      const blob = await exportCompare(compareJobId, 'pdf');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `comparison-${compareJobId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { exportDiff(); } finally { setExporting(false); }
  };
  const exportDocx = async () => {
    if (!compareJobId) { exportDiff(); return; }
    setExporting(true);
    try {
      const blob = await exportCompare(compareJobId, 'docx');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `comparison-${compareJobId}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { exportDiff(); } finally { setExporting(false); }
  };
  const exportClipboard = async () => {
    setExporting(true);
    try {
      if (compareJobId) {
        const blob = await exportCompare(compareJobId, 'text');
        await navigator.clipboard.writeText(await blob.text());
      } else {
        throw new Error('no job');
      }
    } catch {
      const lines = [
        `Comparison: ${origName || 'Original'} -> ${revDisplayName}`,
        summary || '',
        '',
        ...differences.map((d, i) => {
          const meta = TYPE_META[d.type] || TYPE_META.changed;
          return `${i + 1}. [${meta.label}] Page ${d.page}: ${d.description}`;
        }),
      ];
      try { await navigator.clipboard.writeText(lines.join('\n')); } catch { /* ignore */ }
    } finally { setExporting(false); }
  };

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="diff-tab">
      {/* Header: filenames + prev/next nav */}
      <div className="flex-shrink-0 border-b border-slate-200 px-4 py-2 bg-white" data-testid="diff-header">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-slate-700 min-w-0">
            <span className="font-medium text-slate-900 truncate">
              {origName || 'Original'}
            </span>
            <ArrowRight size={14} className="text-slate-400 flex-shrink-0" />
            <span className="font-medium text-slate-900 truncate">
              {revDisplayName}
            </span>
          </div>
          {differences.length > 0 && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => goToDiff(activeDiff - 1)}
                disabled={activeDiff === 0}
                className="btn-secondary !px-2 !py-1 disabled:opacity-40"
                title="Previous difference"
                data-testid="diff-prev"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-sm text-slate-600">
                Difference {activeDiff + 1} of {differences.length}
              </span>
              <button
                onClick={() => goToDiff(activeDiff + 1)}
                disabled={activeDiff === differences.length - 1}
                className="btn-secondary !px-2 !py-1 disabled:opacity-40"
                title="Next difference"
                data-testid="diff-next"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
        {summary && (
          <div className="text-xs text-slate-500 mt-1">
            <div>
              {differences.length} difference
              {differences.length === 1 ? '' : 's'} found.
            </div>
            {summary && (
              <div
                className="prose prose-sm max-w-none mt-1 text-slate-600"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(summary) }}
                data-testid="diff-summary-md"
              />
            )}
          </div>
        )}
      </div>

      {/* Main content: side-by-side viewer (original | new) on the left,
          differences list on the right -> layout: original | new | differences */}
      <div className="flex-1 flex min-h-0 overflow-hidden">
        {hasViewer ? (
          <>
            {/* Side-by-side viewer (original | revision/new) */}
            <div className="flex-1 p-2 min-w-0 overflow-hidden">
              <SideBySideViewer
                ref={viewerRef}
                originalFilename={origName || 'Original'}
                revisionFilename={revDisplayName}
                originalText={origText!.text}
                revisionText={revText!.text}
              />
            </div>
            {/* Differences list on the RIGHT */}
            <div className="w-72 flex-shrink-0 border-l border-slate-200 flex flex-col min-h-0">
              <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Differences ({differences.length})
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={exportPdf}
                    disabled={exporting}
                    className="btn-secondary !px-2 !py-1 text-xs flex items-center gap-1 disabled:opacity-50"
                    title="Download PDF"
                    data-testid="diff-export-pdf"
                  >
                    {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} PDF
                  </button>
                  <button
                    onClick={exportDocx}
                    disabled={exporting}
                    className="btn-secondary !px-2 !py-1 text-xs flex items-center gap-1 disabled:opacity-50"
                    title="Download formatted Word (.docx)"
                    data-testid="diff-export-docx"
                  >
                    {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} .docx
                  </button>
                  <button
                    onClick={exportClipboard}
                    disabled={exporting}
                    className="btn-secondary !px-2 !py-1 text-xs flex items-center gap-1 disabled:opacity-50"
                    title="Copy to clipboard"
                    data-testid="diff-export-clipboard"
                  >
                    <Clipboard size={12} /> Copy
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-1">
                {differences.length === 0 && (
                  <div className="text-sm text-slate-400 text-center py-4">
                    No differences found. The documents are identical.
                  </div>
                )}
                {differences.map((d, i) => {
                  const meta = TYPE_META[d.type] || TYPE_META.changed;
                  const Icon = meta.icon;
                  const isActive = i === activeDiff;
                  return (
                    <button
                      key={i}
                      onClick={() => goToDiff(i)}
                      className={`w-full text-left p-2 rounded-lg border transition-colors ${
                        isActive
                          ? 'border-primary-400 bg-primary-50 ring-1 ring-primary-300'
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                      data-testid={`diff-item-${i}`}
                    >
                      <div className="flex items-start gap-2">
                        <span
                          className={`badge ${meta.cls} flex-shrink-0 text-xs`}
                        >
                          <Icon size={10} className="mr-0.5" /> {meta.label}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="text-xs text-slate-500">
                            Page {d.page}
                          </div>
                          <div
                            className={`text-sm text-slate-800 prose prose-sm max-w-none ${isActive ? 'font-semibold' : ''}`}
                            dangerouslySetInnerHTML={{ __html: renderMarkdown(d.description) }}
                          />
                          {d.source_link && (
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                goToDiff(i);
                              }}
                              className="mt-1 inline-flex items-center gap-1 text-xs text-primary-600 hover:underline cursor-pointer"
                              data-testid={`diff-source-${i}`}
                            >
                              <ExternalLink size={10} /> Source
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          /* Fallback: differences list only (no doc text available) */
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {differences.length === 0 && !summary && (
              <div className="text-center text-slate-400 py-8 text-sm">
                No differences found. The documents are identical.
              </div>
            )}
            {differences.map((d, i) => {
              const meta = TYPE_META[d.type] || TYPE_META.changed;
              const Icon = meta.icon;
              return (
                <div
                  key={i}
                  className="card p-3 flex items-start gap-3"
                  data-testid={`diff-item-${i}`}
                >
                  <span
                    className={`badge ${meta.cls} flex-shrink-0`}
                  >
                    <Icon size={12} className="mr-1" /> {meta.label}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-slate-500">Page {d.page}</div>
                    <div
                      className="text-sm text-slate-800 prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(d.description) }}
                    />
                    {d.source_link && (
                      <button
                        onClick={() => goToDiff(i)}
                        className="mt-1 inline-flex items-center gap-1 text-xs text-primary-600 hover:underline"
                        data-testid={`diff-source-${i}`}
                      >
                        <ExternalLink size={12} /> Source
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {!origDocId && !revDocId && (
              <div className="text-xs text-slate-400 text-center py-4">
                Side-by-side viewer requires document IDs (not available for
                this comparison). Showing differences list only.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}